import React, { useState, useEffect, useReducer, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Avatar from "@material-ui/core/Avatar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import AddIcon from "@material-ui/icons/Add";
import Skeleton from "@material-ui/lab/Skeleton";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { translateMessageMarker } from "../../utils/messagePreview";
import { translateSystemMessage } from "../../helpers/systemMessageText";
import useOpenContactConversation from "../../hooks/useOpenContactConversation";
import { SocketContext } from "../../context/Socket/SocketContext";
import NewTicketModal from "../NewTicketModal";
import ContactModal from "../ContactModal";
import ContactAvatar from "../ContactAvatar";
import MarkdownWrapper from "../MarkdownWrapper";

// Prévia da última mensagem: marcadores de mídia e mensagens de sistema (gravados
// em PT pelo backend) saem no idioma da interface; depois corta em 20 caracteres.
const previewText = (text) => {
  const preview = translateSystemMessage(translateMessageMarker(text));
  return preview.length > 20 ? `${preview.substring(0, 17)}...` : preview;
};

const useStyles = makeStyles((theme) => ({
  contactsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },

  contactsList: {
    flex: 1,
    maxHeight: "100%",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },

  contactItem: {
    cursor: "pointer",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },

  contactName: {
    fontWeight: 500,
    fontSize: 16,
  },

  // ocupa o mesmo slot da prévia quando o contato não tem última mensagem
  contactNumber: {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },

  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  contactLastMessage: {
    paddingRight: 20,
    fontSize: 16,
  },

  lastMessageTime: {
    justifySelf: "flex-end",
    fontSize: 12,
  },

  noContactsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  noContactsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },

  noContactsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  whatsappButton: {
    color: "#25D366",
  },

  newContactButton: {
    width: "100%",
    backgroundColor: "transparent",
    // Cores do tema (e não #000 fixo): no modo escuro texto e "+" ficavam pretos.
    color: theme.palette.text.primary,
    fontWeight: "bold",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderRadius: 0,
    textTransform: "none",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACT") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      // Contato já existe na lista - atualiza
      state[contactIndex] = contact;
      return [...state];
    } else {
      // Contato não existe na lista - NÃO adiciona automaticamente
      // O contato só deve aparecer se corresponder ao filtro atual
      return state;
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const ContactsListPanel = ({ style, searchParam = "", searchBy = "all", tagIds, onSelectContact, active = false }) => {
  const classes = useStyles();
  const history = useHistory();
  const socketManager = useContext(SocketContext);

  const [contacts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  // Fluxo de abrir a conversa do contato (compartilhado com a busca agrupada).
  const {
    openContact,
    loadingContactId: contactLoading,
    pendingContact,
    closeNewTicketModal,
  } = useOpenContactConversation(onSelectContact);

  // Filtro por tags do contato (OU), vindo do popover de filtros da lista.
  const tagIdsKey = JSON.stringify(
    Array.isArray(tagIds)
      ? tagIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
      : []
  );
  const tagIdsRef = useRef(tagIdsKey);
  tagIdsRef.current = tagIdsKey;

  // Reset list when search changes
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, tagIdsKey]);

  // Fetch contacts
  useEffect(() => {
    if (!active) return;

    // PERF: debounce só faz sentido enquanto o usuário digita no campo de busca.
    // Para a abertura da aba (sem busca) ou paginação (scroll), vai direto.
    const debounceMs = searchParam ? 350 : (pageNumber > 1 ? 0 : 50);

    // PERF: só mostra a bolinha se a busca demorar >180ms — evita o flash do spinner.
    let loadingTimer = setTimeout(() => setLoading(true), 180);
    let cancelled = false;

    const abortController = new AbortController();
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", {
            params: {
              searchParam,
              pageNumber,
              searchBy,
              ...(tagIdsKey !== "[]" ? { tagIds: tagIdsKey } : {}),
            },
            signal: abortController.signal,
            timeout: 10000,
          });
          if (cancelled) return;
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          if (loadingTimer) clearTimeout(loadingTimer);
          setLoading(false);
        } catch (err) {
          if (err?.code === "ERR_CANCELED") {
            return;
          }
          if (loadingTimer) clearTimeout(loadingTimer);
          toastError(err);
          setLoading(false);
        }
      };
      fetchContacts();
    }, debounceMs);
    return () => {
      cancelled = true;
      if (loadingTimer) clearTimeout(loadingTimer);
      clearTimeout(delayDebounceFn);
      abortController.abort();
    };
  }, [searchParam, searchBy, pageNumber, active, tagIdsKey]);

  // Socket connection for real-time updates
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update" || data.action === "create") {
        // Com filtro por tags: o contato que perdeu todas as tags filtradas sai
        // da lista. Só quando o evento traz as tags (ex.: edição das tags).
        const filterTagIds = JSON.parse(tagIdsRef.current);
        if (filterTagIds.length > 0 && Array.isArray(data.contact?.tags)) {
          const contactTagIds = data.contact.tags.map((t) => Number(t?.id ?? t));
          if (!filterTagIds.some((id) => contactTagIds.includes(id))) {
            dispatch({ type: "DELETE_CONTACT", payload: +data.contact.id });
            return;
          }
        }
        dispatch({ type: "UPDATE_CONTACT", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prevState) => prevState + 1);
    }
  };

  const handleOpenNewTicketModal = (contact) => {
    openContact(contact);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    closeNewTicketModal();
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const ContactItemSkeleton = () => (
    <ListItem>
      <ListItemAvatar>
        <Skeleton variant="circle" width={40} height={40} />
      </ListItemAvatar>
      <ListItemText
        primary={<Skeleton variant="text" width={120} />}
        secondary={<Skeleton variant="text" width={80} />}
      />
    </ListItem>
  );

  // Handler para quando um novo contato é criado
  const handleContactCreated = (newContact) => {
    setContactModalOpen(false);
    // Abre a conversa com o novo contato
    if (onSelectContact) {
      onSelectContact(newContact);
    }
  };

  return (
    <Paper
      elevation={0}
      className={classes.contactsListWrapper}
      style={style}
    >
      <NewTicketModal
        modalOpen={!!pendingContact}
        initialContact={pendingContact}
        onClose={(ticket) => handleCloseOrOpenTicket(ticket)}
      />

      <ContactModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        onSave={handleContactCreated}
        aria-labelledby="form-dialog-title"
      />

      <Button
        className={classes.newContactButton}
        onClick={() => setContactModalOpen(true)}
        endIcon={<AddIcon />}
      >
        {i18n.t("contactsListPanel.newContact")}
      </Button>
      <Divider />

      <List className={classes.contactsList} onScroll={handleScroll}>
        {contacts.length === 0 && !loading ? (
          <div className={classes.noContactsDiv}>
            <span className={classes.noContactsTitle}>
              {i18n.t("contactsListPanel.noContacts")}
            </span>
            <p className={classes.noContactsText}>
              {i18n.t("contactsListPanel.noContactsText")}
            </p>
          </div>
        ) : (
          <>
            {contacts.map((contact) => (
              <ListItem
                key={contact.id}
                className={classes.contactItem}
                onClick={() => handleOpenNewTicketModal(contact)}
                disabled={contactLoading === contact.id}
              >
                <ListItemAvatar>
                  {contactLoading === contact.id ? (
                    <CircularProgress size={40} />
                  ) : (
                    <ContactAvatar contact={contact} />
                  )}
                </ListItemAvatar>
                <ListItemText
                  disableTypography
                  primary={
                    <span className={classes.contactNameWrapper}>
                      <Typography
                        noWrap
                        component="span"
                        variant="body2"
                        color="textPrimary"
                        className={classes.contactName}
                      >
                        {contact.name}
                      </Typography>
                      {contact.lastMessageTime && (
                        <Typography
                          className={classes.lastMessageTime}
                          component="span"
                          variant="body2"
                          color="textSecondary"
                        >
                          {isSameDay(parseISO(contact.lastMessageTime), new Date()) ? (
                            <>{format(parseISO(contact.lastMessageTime), "HH:mm")}</>
                          ) : (
                            <>{format(parseISO(contact.lastMessageTime), "dd/MM/yyyy")}</>
                          )}
                        </Typography>
                      )}
                    </span>
                  }
                  secondary={
                    contact.lastMessage ? (
                      <Typography
                        className={classes.contactLastMessage}
                        noWrap
                        component="span"
                        variant="body2"
                        color="textSecondary"
                      >
                        <MarkdownWrapper>
                          {previewText(contact.lastMessage)}
                        </MarkdownWrapper>
                      </Typography>
                    ) : (
                      // Fallback: exibe número se não houver última mensagem e não for grupo
                      !contact.isGroup && contact.number && contact.number.length <= 15 ? (
                        <Typography className={classes.contactNumber}>
                          {contact.number}
                        </Typography>
                      ) : null
                    )
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    className={classes.whatsappButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenNewTicketModal(contact);
                    }}
                    disabled={contactLoading === contact.id}
                  >
                    <WhatsAppIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {loading && (
              <>
                <ContactItemSkeleton />
                <ContactItemSkeleton />
                <ContactItemSkeleton />
              </>
            )}
          </>
        )}
      </List>
    </Paper>
  );
};

export default ContactsListPanel;
